// CameraView.js
// Staff-only camera interface for photographing completed pancakes.
// Defaults to the active shift's awaiting-photo orders.
// Past shifts' orders are grouped by shift (collapsible), sorted by date.
// Each order has two photo buttons: 📷 launches the phone camera directly
// (capture="environment" — bypasses Android's image-picker chooser, so Google
// Photos can't hijack the camera), 🖼 picks from the photo library. Both go
// through a framing step (pinch/pan + circle overlay) before upload.

import { useState, useEffect, useRef } from "react";
import { db, storage } from "./firebase";
import useActiveShift from "./hooks/useActiveShift";
import ShiftPicker, { ShiftChip } from "./components/ShiftPicker";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection, query, where, onSnapshot,
  doc, updateDoc, setDoc, serverTimestamp, getDocs, documentId, increment,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import PreCropTool from "./components/PreCropTool";
import { addOnDeliveryPrompt, fulfillAllAddOns } from "./utils/combos";

import { STAFF_THEME, FONT } from "./theme/staff";
const BRAND = STAFF_THEME;

export default function CameraView({ user, navigate }) {
  const [orders,        setOrders]        = useState([]);
  const [activeShift,   setActiveShift]   = useState(null);
  const [shiftLoading,  setShiftLoading]  = useState(true);
  const [loading,       setLoading]       = useState(true);
  const [pastShiftMeta, setPastShiftMeta] = useState({});
  const [expanded,      setExpanded]      = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [phase,         setPhase]         = useState("list");
  const [capturedBlob,  setCapturedBlob]  = useState(null);
  const [capturedUrl,   setCapturedUrl]   = useState(null);
  const [showCrop,      setShowCrop]      = useState(false);
  const [uploadPct,     setUploadPct]     = useState(0);
  const [error,         setError]         = useState(null);

  // File upload / framing refs
  const cameraInputRef       = useRef(null);   // capture="environment" — launches the phone camera
  const libraryInputRef      = useRef(null);   // plain picker — choose from the photo library
  const pendingOrderRef      = useRef(null);
  const pendingSourceRef     = useRef(null);   // "camera" | "library" — used to reopen the right picker
  const originalFileRef      = useRef(null);   // full-frame camera capture, kept for the Save-to-phone fallback
  const captureAtRef         = useRef(null);   // ms timestamp of the shutter — ends the cook clock at capture, not upload
  const [shareErr, setShareErr] = useState(null);
  const [framingImg, setFramingImg]     = useState(null);       // HTMLImageElement
  const [cSize, setCSize]               = useState(() => window.innerWidth);
  const framingXformRef                 = useRef({ scale: 1, tx: 0, ty: 0, rot: 0 });
  const [framingXform, setFramingXform] = useState({ scale: 1, tx: 0, ty: 0, rot: 0 });
  const framingContainerRef             = useRef(null);
  const framingElRef                    = useRef(null);
  const framingGestureStateRef          = useRef(null);

  // ── LISTENERS ────────────────────────────────────────────────────────────────
  // Which booth is this camera working? Same resolver, picker and chip as the
  // queue and register — the camera ATTACHES PHOTOS and hands pancakes off, so
  // it must never be ambiguous about whose griddle it's looking at.
  const {
    shift: scopedShift,
    loading: scopeLoading,
    candidates: activeCandidates,
    needsChoice: needsShiftChoice,
    isConcurrent,
    bind: bindShift,
  } = useActiveShift();
  const [showShiftPicker, setShowShiftPicker] = useState(false);

  useEffect(() => {
    setActiveShift(scopedShift);
    setShiftLoading(scopeLoading);
  }, [scopedShift, scopeLoading]);

  useEffect(() => {
    const q = query(collection(db, "orders"), where("state", "in", ["flipping", "awaiting_photo", "ready_to_deliver"]));
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => !o.trashedAt);
      list.sort((a, b) => (a.completedAt?.toMillis?.() ?? 0) - (b.completedAt?.toMillis?.() ?? 0));
      setOrders(list);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const pastIds = [...new Set(
      orders
        .filter(o => !activeShift || o.shiftId !== activeShift.id)
        .map(o => o.shiftId).filter(Boolean)
    )];
    if (pastIds.length === 0) return;
    getDocs(query(collection(db, "shift_summaries"), where(documentId(), "in", pastIds.slice(0, 30))))
      .then(snap => {
        const meta = {};
        snap.docs.forEach(d => { meta[d.id] = d.data(); });
        setPastShiftMeta(prev => ({ ...prev, ...meta }));
      })
      .catch(() => {});
  }, [orders.length, activeShift?.id]);

  // ── FRAMING: measure container after phase change ─────────────────────────────
  useEffect(() => {
    if (phase !== "framing") return;
    const el = framingContainerRef.current;
    if (!el) return;
    const s = Math.min(el.offsetWidth, el.offsetHeight);
    if (s > 0) setCSize(s);
  }, [phase, framingImg]);

  // ── FRAMING: touch gestures ───────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "framing" || cSize === 0) return;
    const el = framingContainerRef.current;
    if (!el) return;

    function getDist(t1, t2) { return Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY); }
    function getMid(t1, t2)  { return { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 }; }
    function getAngle(t1, t2) { return Math.atan2(t2.clientY - t1.clientY, t2.clientX - t1.clientX) * 180 / Math.PI; }

    function applyXform(next) {
      framingXformRef.current = next;
      if (framingElRef.current) {
        framingElRef.current.style.transform = `translate(${next.tx}px, ${next.ty}px) scale(${next.scale}) rotate(${next.rot}deg)`;
      }
    }

    function onTouchStart(e) {
      e.preventDefault();
      const cur  = framingXformRef.current;
      const rect = el.getBoundingClientRect();
      const cx   = rect.left + rect.width  / 2;
      const cy   = rect.top  + rect.height / 2;
      if (e.touches.length >= 2) {
        framingGestureStateRef.current = {
          type: "pinch",
          startDist:  getDist(e.touches[0], e.touches[1]),
          startMid:   getMid(e.touches[0],  e.touches[1]),
          startAngle: getAngle(e.touches[0], e.touches[1]),
          startXform: { ...cur }, cx, cy,
        };
      } else {
        framingGestureStateRef.current = {
          type: "pan",
          startX: e.touches[0].clientX, startY: e.touches[0].clientY,
          startXform: { ...cur },
        };
      }
    }

    function onTouchMove(e) {
      const g = framingGestureStateRef.current;
      if (!g) return;
      e.preventDefault();
      if (g.type === "pinch" && e.touches.length >= 2) {
        // Pinch zooms AND twists at once — free rotation, no snapping. The
        // photo point that started under the fingers stays under them:
        // t1 = (mid1 − c) − k·R(Δθ)·(mid0 − c − t0), k = scale ratio.
        const newDist  = getDist(e.touches[0], e.touches[1]);
        const newMid   = getMid(e.touches[0],  e.touches[1]);
        const rawScale = g.startXform.scale * (newDist / g.startDist);
        const newScale = Math.max(0.1, Math.min(15, rawScale));
        const k        = newScale / g.startXform.scale;
        let dRot       = getAngle(e.touches[0], e.touches[1]) - g.startAngle;
        if (dRot > 180) dRot -= 360; else if (dRot < -180) dRot += 360;
        const rad = dRot * Math.PI / 180, cos = Math.cos(rad), sin = Math.sin(rad);
        const dx  = g.startMid.x - g.cx - g.startXform.tx;
        const dy  = g.startMid.y - g.cy - g.startXform.ty;
        applyXform({
          scale: newScale,
          rot:   g.startXform.rot + dRot,
          tx: newMid.x - g.cx - k * (cos * dx - sin * dy),
          ty: newMid.y - g.cy - k * (sin * dx + cos * dy),
        });
      } else if (g.type === "pan" && e.touches.length === 1) {
        applyXform({
          ...g.startXform,
          tx: g.startXform.tx + (e.touches[0].clientX - g.startX),
          ty: g.startXform.ty + (e.touches[0].clientY - g.startY),
        });
      }
    }

    function onTouchEnd() {
      framingGestureStateRef.current = null;
      setFramingXform({ ...framingXformRef.current });
    }

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
    };
  }, [phase, cSize]);

  // ── PHOTO PICKERS ─────────────────────────────────────────────────────────────
  // Two paths: 📷 launches the phone camera directly (capture="environment"),
  // 🖼 opens the photo library. Both feed handleFileSelected → framing.
  function openCamera(order) {
    pendingOrderRef.current  = order;
    pendingSourceRef.current = "camera";
    cameraInputRef.current?.click();
  }
  function openLibrary(order) {
    pendingOrderRef.current  = order;
    pendingSourceRef.current = "library";
    libraryInputRef.current?.click();
  }

  // Fallback path to get a camera shot into the phone's gallery. A PWA can't
  // write to the gallery directly, but the Web Share sheet lets the user pick
  // a gallery / "Save to device" target. Uses the original full-frame file.
  // Only meaningful for camera captures — library photos are already on-device.
  async function saveToPhone() {
    const file = originalFileRef.current;
    if (!file) return;
    setShareErr(null);
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Pancake photo" });
      } else {
        setShareErr("This phone's browser can't share files — use your camera app to keep a copy.");
      }
    } catch (e) {
      // AbortError = user dismissed the sheet; ignore. Anything else, surface it.
      if (e?.name !== "AbortError") setShareErr("Couldn't open the share sheet.");
    }
  }

  async function markDelivered(order) {
    // Delivery checkpoint — same guard as ArtistQueueView's markDelivered:
    // combo goods change hands here, Cancel aborts the delivery.
    const addOnPrompt = addOnDeliveryPrompt(order);
    if (addOnPrompt && !window.confirm(addOnPrompt)) return;
    await updateDoc(doc(db, "orders", order.id), {
      state: "done",
      deliveredAt: serverTimestamp(),
      ...(addOnPrompt ? { addOns: fulfillAllAddOns(order) } : {}),
    });
    if (activeShift) updateDoc(doc(db, "shifts", activeShift.id), { lastActivityAt: serverTimestamp() }).catch(() => {});
  }

  // Content pieces don't have a customer to hand off to. After the photo step,
  // staff decide: preserve it (enters preservation pipeline at /studio) or
  // just mark done (no preservation, vanishes from the active studio queue).
  async function markContentDone(order, preserve) {
    await updateDoc(doc(db, "orders", order.id), {
      state: "done",
      deliveredAt: serverTimestamp(),
      ...(preserve ? { isPreservation: true, preservationStatus: "pending", aftercare: "preserve" } : {}),
    });
  }

  function handleFileSelected(e) {
    const file = e.target.files?.[0];
    if (!file || !pendingOrderRef.current) return;
    e.target.value = "";
    // Shutter moment — the cook clock ends here, not at upload. Only true for a
    // live camera shot: a camera-roll pick can happen hours later (offline
    // event, uploads caught up at night), and timing to the pick recorded
    // 8-hour cooks. No time beats a wrong one.
    captureAtRef.current = pendingSourceRef.current === "camera" ? Date.now() : null;
    setSelectedOrder(pendingOrderRef.current);
    const url = URL.createObjectURL(file);
    setCapturedUrl(url);
    setCapturedBlob(file); // keep original as fallback; cropAndPreview replaces it
    originalFileRef.current = pendingSourceRef.current === "camera" ? file : null; // only offer Save-to-phone for camera shots
    // Load image element to get natural dimensions for framing math
    const img = new window.Image();
    img.onload = () => {
      setFramingImg(img);
      framingXformRef.current = { scale: 1, tx: 0, ty: 0, rot: 0 };
      setFramingXform({ scale: 1, tx: 0, ty: 0, rot: 0 });
    };
    img.src = url;
    setError(null);
    setPhase("framing");
  }

  // Crop the image to the framed square and advance to preview.
  // Math: the image wrapper is centered in the cSize×cSize container.
  // baseScale = min(cSize/iw, cSize/ih) — the "objectFit: contain" scale.
  // totalScale = baseScale × user scale.
  // Container corner [0,0] maps to image pixel (iw/2 - (cSize/2+tx)/totalScale, ih/2 - (cSize/2+ty)/totalScale).
  // Crop the image to the framed square and advance to preview.
  // The canvas replays the exact on-screen transform: the container square is
  // the output, the image wrapper is centered in it at its "contain" size
  // (baseScale), then translate → scale → rotate about the center — the same
  // order as the CSS transform, so what's in the circle is what uploads.
  function cropAndPreview() {
    if (!framingImg || cSize === 0) return;
    const iw = framingImg.naturalWidth, ih = framingImg.naturalHeight;
    const baseScale  = Math.min(cSize / iw, cSize / ih);
    const { scale, tx, ty, rot } = framingXformRef.current;
    const srcSize    = cSize / (baseScale * scale);

    const outputSize = Math.min(Math.round(Math.max(srcSize, 100)), 1920);
    const canvas     = document.createElement("canvas");
    canvas.width = outputSize; canvas.height = outputSize;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, outputSize, outputSize);

    const renderedW = Math.round(iw * baseScale);
    const renderedH = Math.round(ih * baseScale);
    ctx.scale(outputSize / cSize, outputSize / cSize);
    ctx.translate(cSize / 2 + tx, cSize / 2 + ty);
    ctx.scale(scale, scale);
    ctx.rotate((rot || 0) * Math.PI / 180);
    ctx.drawImage(framingImg, -renderedW / 2, -renderedH / 2, renderedW, renderedH);

    canvas.toBlob(blob => {
      setCapturedBlob(blob);
      setCapturedUrl(URL.createObjectURL(blob));
      setPhase("preview");
    }, "image/jpeg", 0.92);
  }

  // ── UPLOAD TO STORAGE / FIRESTORE ─────────────────────────────────────────────
  async function upload() {
    if (!capturedBlob || !selectedOrder) return;
    setPhase("uploading");
    setUploadPct(10);
    setError(null);

    try {
      const tokenId = selectedOrder.tokenId ?? selectedOrder.id;
      const orderId = selectedOrder.id;
      const ts      = Date.now();

      const origRef = storageRef(storage, `orders/${tokenId}/pancake_original_${ts}.jpg`);
      await uploadBytes(origRef, capturedBlob, { contentType: "image/jpeg" });
      const photoUrl = await getDownloadURL(origRef);
      setUploadPct(60);

      // Cook time ends at the shutter (captureAtRef), not now — framing + review +
      // this upload can add many seconds. If the artist already tapped "Off the
      // Griddle" (cookTimeMs set), keep that value; never overwrite a locked one.
      const cookStart = selectedOrder.cookingStartedAt || selectedOrder.flippedAt;
      const cookEndMs = captureAtRef.current;
      const cookTimeMs = selectedOrder.cookTimeMs != null
        ? null // already locked upstream — don't rewrite
        : (cookStart && cookEndMs ? cookEndMs - cookStart.toMillis() : null);
      // Aftercare routing: deliver → ready_to_deliver (hand to guest),
      // preserve/ship/refrigerate → done (enters post-griddle pipeline)
      // awaiting_photo = already handed off at a closed shift, only the photo
      // was missing — there's no guest left to hand it to, so straight to done.
      const aftercare = selectedOrder.aftercare || "deliver";
      const isPreserve = aftercare === "preserve" || aftercare === "ship" || aftercare === "refrigerate";
      const isFinished = isPreserve || selectedOrder.state === "awaiting_photo";
      // Re-take on a pancake that already had a photo (wrong photo, bad shot):
      // drop the old cutout/thumbs/AI text so they regenerate from the new one.
      const isRetake = !!selectedOrder.photo_url;
      await updateDoc(doc(db, "orders", orderId), {
        photo_url: photoUrl,
        ...(isRetake ? {
          photo_cutout_url: null, photo_thumb_url: null, cutoutThumbnailUrl: null,
          thumbnailUrl: null, ai_description: null, ai_tags: null,
        } : {}),
        state: isFinished ? "done" : "ready_to_deliver",
        photographedAt: serverTimestamp(),
        readyToDeliverAt: serverTimestamp(),
        ...(selectedOrder.state === "awaiting_photo" ? {} : { completedAt: isPreserve ? serverTimestamp() : null }),
        ...(cookTimeMs != null ? { cookTimeMs } : {}),
        ...(isPreserve ? { preservationStatus: "drying" } : {}),
      });
      setUploadPct(85);

      try {
        httpsCallable(getFunctions(getApp()), "generatePancakeDescription")({
          orderId, photoUrl, tokenId,
          subjectName:    selectedOrder.subjectName   || null,
          recipientName:  selectedOrder.recipientName || selectedOrder.customerName || null,
          notes:          selectedOrder.notes         || null,
          orderType:      selectedOrder.orderType     || null,
          designLabel:    selectedOrder.designLabel    || null,
          favoriteColors: selectedOrder.favoriteColors || null,
          isFreestyle:    selectedOrder.isFreestyle    || false,
          artistName:     selectedOrder.artistName    || null,
          drawingTimeMs:  selectedOrder.drawingTimeMs || null,
          cookTimeMs:     selectedOrder.cookTimeMs ?? cookTimeMs ?? null,
        });
      } catch { /* non-fatal */ }

      setUploadPct(100);
      setPhase("complete");
    } catch (err) {
      console.error("Upload failed:", err);
      setError(err.message);
      setPhase("preview");
    }
  }

  async function retireShift(shiftId) {
    // setDoc+merge creates the doc if it doesn't exist (old shifts predate shift_summaries)
    await setDoc(doc(db, "shift_summaries", shiftId), { photosRetired: true }, { merge: true });
    setPastShiftMeta(prev => ({ ...prev, [shiftId]: { ...prev[shiftId], photosRetired: true } }));
  }

  function backToList() {
    setCapturedBlob(null); setCapturedUrl(null); setUploadPct(0);
    setSelectedOrder(null); setError(null);
    setFramingImg(null); pendingOrderRef.current = null; pendingSourceRef.current = null;
    originalFileRef.current = null; setShareErr(null);
    framingXformRef.current = { scale: 1, tx: 0, ty: 0, rot: 0 };
    setFramingXform({ scale: 1, tx: 0, ty: 0, rot: 0 });
    setPhase("list");
  }

  // ── FRAMING ───────────────────────────────────────────────────────────────────
  if (phase === "framing") {
    const iw         = framingImg?.naturalWidth  ?? 1;
    const ih         = framingImg?.naturalHeight ?? 1;
    const baseScale  = cSize > 0 ? Math.min(cSize / iw, cSize / ih) : 1;
    const renderedW  = Math.round(iw * baseScale);
    const renderedH  = Math.round(ih * baseScale);
    const name       = selectedOrder?.subjectName || selectedOrder?.recipientName || selectedOrder?.customerName;

    return (
      <div style={{ position: "fixed", inset: 0, background: "#000", display: "flex", flexDirection: "column", touchAction: "none", fontFamily: FONT.body }}>
        <div style={{ padding: "0.6rem 1rem", background: "#0a1230", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, borderBottom: `1px solid ${BRAND.border}` }}>
          <button onClick={backToList} style={hud}>Cancel</button>
          <span style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.9rem" }}>
            #{selectedOrder?.queueNumber} · {name}
          </span>
          <button
            onClick={cropAndPreview}
            style={{ background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "8px", padding: "0.45rem 1rem", fontWeight: "bold", fontSize: "0.9rem", cursor: "pointer" }}
          >
            Use This ›
          </button>
        </div>

        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "#000" }}>
          <div
            ref={framingContainerRef}
            style={{ width: "100%", aspectRatio: "1 / 1", position: "relative", overflow: "hidden", touchAction: "none" }}
          >
            {cSize > 0 && framingImg && (
              <div
                ref={framingElRef}
                style={{
                  position: "absolute",
                  width:  renderedW,
                  height: renderedH,
                  left:   (cSize - renderedW) / 2,
                  top:    (cSize - renderedH) / 2,
                  transform: `translate(${framingXform.tx}px, ${framingXform.ty}px) scale(${framingXform.scale}) rotate(${framingXform.rot}deg)`,
                  transformOrigin: "center center",
                  willChange: "transform",
                }}
              >
                <img src={capturedUrl} alt="" style={{ width: "100%", height: "100%", display: "block", userSelect: "none" }} />
              </div>
            )}
            {/* Circle overlay — same proportions as viewfinder */}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
              <path fillRule="evenodd"
                d="M 0 0 H 100 V 100 H 0 Z M 50 4 A 46 46 0 1 0 50 96 A 46 46 0 1 0 50 4 Z"
                fill="rgba(0,0,0,0.52)" />
              <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(252,179,31,0.75)" strokeWidth="0.9" />
              <line x1="33" y1="4"  x2="33" y2="96" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
              <line x1="67" y1="4"  x2="67" y2="96" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
              <line x1="4"  y1="33" x2="96" y2="33" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
              <line x1="4"  y1="67" x2="96" y2="67" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
            </svg>
          </div>
        </div>

        <div style={{ padding: "0.65rem 1rem", background: "#0a1230", borderTop: `1px solid ${BRAND.border}`, flexShrink: 0, textAlign: "center" }}>
          <p style={{ color: BRAND.border, fontSize: "0.78rem", margin: 0 }}>Pinch to zoom · twist to rotate · drag to reposition</p>
          {Math.abs(framingXform.rot % 360) > 0.5 && (
            <button
              onClick={() => { const next = { ...framingXformRef.current, rot: 0 }; framingXformRef.current = next; setFramingXform(next); }}
              style={{ ...hud, marginTop: "0.45rem" }}
            >↺ Straighten ({Math.round(framingXform.rot)}°)</button>
          )}
        </div>
      </div>
    );
  }

  // ── PREVIEW ──────────────────────────────────────────────────────────────────
  if (phase === "preview") {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#000", display: "flex", flexDirection: "column", fontFamily: FONT.body }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {capturedUrl && <img src={capturedUrl} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
        </div>
        {error && <div style={{ background: "#300", color: "#f88", padding: "0.5rem 1rem", fontSize: "0.85rem", textAlign: "center" }}>{error}</div>}
        <div style={{ display: "flex", gap: "0.6rem", padding: "1.25rem", background: "#0a1230", borderTop: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
          <button onClick={() => { setCapturedBlob(null); setCapturedUrl(null); setFramingImg(null); framingXformRef.current = { scale: 1, tx: 0, ty: 0, rot: 0 }; setFramingXform({ scale: 1, tx: 0, ty: 0, rot: 0 }); (pendingSourceRef.current === "library" ? libraryInputRef : cameraInputRef).current?.click(); }} style={btn(BRAND.card, BRAND.muted)}>Pick Different</button>
          <button onClick={() => setShowCrop(true)} style={btn(BRAND.gold, BRAND.dark)}>📐 Crop</button>
          <button onClick={upload} style={{ ...btn(BRAND.green, "white"), flex: 1 }}>Upload & Done ✓</button>
        </div>

        {showCrop && capturedUrl && (
          <PreCropTool
            src={capturedUrl}
            title="Crop the pancake"
            subtitle="Tighter framing means a cleaner cutout."
            onConfirm={(blob) => {
              setShowCrop(false);
              if (!blob) return;
              if (capturedUrl) URL.revokeObjectURL(capturedUrl);
              setCapturedBlob(blob);
              setCapturedUrl(URL.createObjectURL(blob));
            }}
            onCancel={() => setShowCrop(false)}
          />
        )}
      </div>
    );
  }

  // ── UPLOADING ─────────────────────────────────────────────────────────────────
  if (phase === "uploading") {
    return (
      <div style={{ position: "fixed", inset: 0, background: BRAND.dark, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1rem", fontFamily: FONT.body }}>
        <div style={{ color: "white", fontSize: "1rem" }}>Uploading…</div>
        <div style={{ width: "240px", height: "6px", background: BRAND.card, borderRadius: "3px", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${uploadPct}%`, background: BRAND.gold, borderRadius: "3px", transition: "width 0.4s ease" }} />
        </div>
      </div>
    );
  }

  // ── COMPLETE ──────────────────────────────────────────────────────────────────
  if (phase === "complete") {
    const name = selectedOrder?.subjectName || selectedOrder?.recipientName || selectedOrder?.customerName;
    return (
      <div style={{ position: "fixed", inset: 0, background: BRAND.dark, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1.25rem", fontFamily: FONT.body, padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "3rem" }}>🥞</div>
        <div style={{ color: BRAND.gold, fontSize: "1.4rem", fontWeight: "bold" }}>Photo saved!</div>
        <div style={{ color: BRAND.muted, fontSize: "0.9rem" }}>#{selectedOrder?.queueNumber} · {name}</div>
        <div style={{ color: BRAND.dim, fontSize: "0.8rem", maxWidth: "260px", lineHeight: 1.5 }}>
          AI description + cutout generating in background — check the portal in ~30 seconds.
        </div>
        {originalFileRef.current && typeof navigator !== "undefined" && navigator.share && (
          <button onClick={saveToPhone} style={{ ...btn(BRAND.card, BRAND.muted), marginTop: "0.25rem", minWidth: "180px" }}>📲 Save to phone</button>
        )}
        {shareErr && <div style={{ color: "#f88", fontSize: "0.78rem", maxWidth: "260px" }}>{shareErr}</div>}
        <button onClick={backToList} style={{ ...btn(BRAND.gold, BRAND.dark), marginTop: "0.5rem", minWidth: "180px" }}>← Next Pancake</button>
      </div>
    );
  }

  // ── LIST ──────────────────────────────────────────────────────────────────────
  const currentOrders = activeShift ? orders.filter(o => o.shiftId === activeShift.id)  : [];

  // Orders belonging to ANOTHER LIVE booth are not "past shifts" — they're
  // someone else's queue, cooking right now, 20 miles away (2026-09-25).
  // The orders listener is deliberately unfiltered (it has to reach closed
  // shifts for catch-up), so before concurrent events every non-current order
  // really was historical. Now the other venue's live queue would land in the
  // "Past Shifts" list with working camera / hand-off / preserve buttons —
  // the exact mistake this whole scoping change exists to prevent.
  const otherLiveShiftIds = new Set(
    (activeCandidates || []).filter(sh => sh.id !== activeShift?.id).map(sh => sh.id)
  );
  const pastOrders    = orders.filter(o =>
    (!activeShift || o.shiftId !== activeShift.id) && !otherLiveShiftIds.has(o.shiftId)
  );
  const pastGroups    = pastOrders.reduce((acc, o) => {
    const k = o.shiftId ?? "__unknown__";
    (acc[k] = acc[k] || []).push(o);
    return acc;
  }, {});
  const sortedGroupKeys = Object.keys(pastGroups)
    .filter(k => !pastShiftMeta[k]?.photosRetired)
    .sort((a, b) => {
      const aMs = pastShiftMeta[a]?.closedAt?.toMillis?.() ?? 0;
      const bMs = pastShiftMeta[b]?.closedAt?.toMillis?.() ?? 0;
      return bMs - aMs;
    });

  // Two booths live and this device hasn't said which it's working — block
  // before a photo can be attached to the wrong venue's pancake.
  if (needsShiftChoice || showShiftPicker) {
    return (
      <ShiftPicker
        candidates={activeCandidates}
        audience="staff"
        title="Which booth is this camera?"
        subtitle="Two queues are open. You'll only see pancakes waiting on the booth you pick."
        onPick={(sh) => { bindShift(sh); setActiveShift(sh); setShowShiftPicker(false); }}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: BRAND.dark, fontFamily: FONT.body, display: "flex", flexDirection: "column" }}>
      <input ref={cameraInputRef}  type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleFileSelected} />
      <input ref={libraryInputRef} type="file" accept="image/*"                        style={{ display: "none" }} onChange={handleFileSelected} />

      {/* flexWrap + rowGap so the booth chip can break onto its own row on an
          iPhone instead of pushing ← Menu off-screen (same fix ArtistQueueView's
          top bar already carries). */}
      <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, display: "flex", alignItems: "center", gap: "0.75rem", rowGap: "0.5rem", flexWrap: "wrap" }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem", flexShrink: 0 }}>📷 Camera</div>
        <div style={{ color: BRAND.dim, fontSize: "0.75rem", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email}</div>
        <ShiftChip shift={activeShift} isConcurrent={isConcurrent} onChange={() => setShowShiftPicker(true)} style={{ flexShrink: 0 }} />
        <button onClick={() => navigate("/menu")} style={{ ...hud, flexShrink: 0 }}>← Menu</button>
      </div>

      {error && <div style={{ background: "#300", color: "#f88", padding: "0.75rem 1rem", fontSize: "0.85rem", textAlign: "center" }}>{error}</div>}

      {(loading || shiftLoading) ? (
        <div style={{ color: BRAND.dim, padding: "3rem", textAlign: "center" }}>Loading…</div>
      ) : (
        <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>

          {/* Current shift */}
          <div>
            <div style={sectionHeader}>
              <span style={{ color: activeShift ? "#4caf50" : BRAND.dim }}>●</span>
              {" "}{activeShift ? (activeShift.eventName ?? "Current Shift") : "No active shift"}
              <span style={{ color: BRAND.dim, fontWeight: "normal", marginLeft: "0.5rem" }}>
                {currentOrders.length > 0 ? `— ${currentOrders.length} waiting` : "— nothing waiting"}
              </span>
            </div>
            {currentOrders.length === 0 ? (
              <div style={{ color: BRAND.sky, fontSize: "0.85rem", padding: "0.5rem 0" }}>✓ All caught up</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {currentOrders.map(order => (
                  <OrderRow key={order.id} order={order}
                    onCamera={() => openCamera(order)}
                    onLibrary={() => openLibrary(order)}
                    onHandOff={order.state === "ready_to_deliver" && order.priority !== "content" ? () => markDelivered(order) : null}
                    onPreserve={order.state === "ready_to_deliver" && order.priority === "content" ? () => markContentDone(order, true) : null}
                    onDiscard={order.state === "ready_to_deliver" && order.priority === "content" ? () => markContentDone(order, false) : null} />
                ))}
              </div>
            )}
          </div>

          {/* Past shifts */}
          {sortedGroupKeys.length > 0 && (
            <div>
              <div style={sectionHeader}>
                Past Shifts
                <span style={{ color: BRAND.dim, fontWeight: "normal", marginLeft: "0.5rem" }}>— {pastOrders.length} waiting</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {sortedGroupKeys.map(shiftId => {
                  const meta        = pastShiftMeta[shiftId];
                  const groupOrders = pastGroups[shiftId];
                  const isOpen      = expanded[shiftId] ?? false;
                  const label       = meta?.eventName ?? "Unnamed Shift";
                  const dateStr     = meta?.closedAt
                    ? new Date(meta.closedAt.toMillis()).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                    : null;
                  return (
                    <div key={shiftId} style={{ background: BRAND.card, borderRadius: "10px", border: `1px solid ${BRAND.border}`, overflow: "hidden" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <button
                          onClick={() => setExpanded(p => ({ ...p, [shiftId]: !isOpen }))}
                          style={{ flex: 1, background: "none", border: "none", cursor: "pointer", padding: "0.7rem 1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        >
                          <div style={{ textAlign: "left" }}>
                            <div style={{ color: BRAND.muted, fontSize: "0.85rem", fontWeight: "bold" }}>{label}</div>
                            {dateStr && <div style={{ color: BRAND.border, fontSize: "0.72rem" }}>{dateStr}</div>}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <span style={{ color: BRAND.dim, fontSize: "0.8rem" }}>{groupOrders.length} 🥞</span>
                            <span style={{ color: BRAND.border, fontSize: "0.7rem" }}>{isOpen ? "▲" : "▼"}</span>
                          </div>
                        </button>
                        <button
                          onClick={() => { if (window.confirm("Archive this shift? It will be hidden from the camera list.")) retireShift(shiftId); }}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: "0.7rem 0.85rem", color: BRAND.dim, fontSize: "0.72rem", fontWeight: "bold", letterSpacing: "0.03em", whiteSpace: "nowrap", borderLeft: `1px solid ${BRAND.divider}` }}
                          title="No more photos for this shift"
                        >
                          Archive
                        </button>
                      </div>
                      {isOpen && (
                        <div style={{ padding: "0 0.75rem 0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem", borderTop: `1px solid ${BRAND.divider}` }}>
                          {groupOrders.map(order => (
                            <OrderRow key={order.id} order={order}
                              onCamera={() => openCamera(order)}
                              onLibrary={() => openLibrary(order)} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {orders.length === 0 && (
            <div style={{ color: BRAND.dim, padding: "2rem", textAlign: "center" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>✓</div>
              No pancakes waiting for a photo
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── ORDER ROW ─────────────────────────────────────────────────────────────────
function OrderRow({ order, onCamera, onLibrary, onHandOff, onPreserve, onDiscard }) {
  const name      = order.subjectName || order.recipientName || order.customerName;
  const typeLabel = order.orderType === "flash" ? "⚡ Flash" : order.orderType === "freestyle_flash" ? "⚡ Freestyle" : "🎨 Custom";
  const isReady   = order.state === "ready_to_deliver";
  const isContent = order.priority === "content";
  return (
    <div style={{ background: BRAND.raised, borderRadius: "10px", padding: "0.85rem 1rem", display: "flex", alignItems: "center", gap: "0.85rem", border: `1px solid ${isReady ? "rgba(112,190,68,0.4)" : BRAND.border}` }}>
      <div style={{ background: isReady ? "#70be44" : "#fcb31f", color: BRAND.dark, fontWeight: "bold", fontSize: "1rem", width: "40px", height: "40px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {isReady ? "✓" : (order.queueNumber ?? "—")}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "white", fontWeight: "bold", fontSize: "0.95rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
        {order.subjectName && <div style={{ color: BRAND.muted, fontSize: "0.78rem" }}>for {order.recipientName}</div>}
        <div style={{ color: BRAND.dim, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: "0.1rem" }}>{typeLabel}</div>
        {order.artistName && <div style={{ color: BRAND.sky, fontSize: "0.72rem", marginTop: "0.1rem" }}>🎨 {order.artistName}</div>}
      </div>
      {/* Once photographed, show THE PHOTO — that's how a wrong one gets spotted. */}
      {((isReady && order.photo_url) || order.croppedImageUrl || order.imageUrl) && (
        <img
          src={(isReady && order.photo_url) || order.croppedImageUrl || order.imageUrl}
          alt={isReady && order.photo_url ? "photo" : "reference"}
          style={{ width: "48px", height: "48px", borderRadius: "6px", objectFit: "cover", flexShrink: 0, border: `1px solid ${BRAND.border}` }}
        />
      )}
      {isReady && isContent && onPreserve && onDiscard ? (
        <>
          <button onClick={onDiscard} style={actionBtn("#2a1010", "#ef537e", "1px solid #5a2030")} title="Mark done — no preservation">🗑</button>
          <button onClick={onPreserve} style={actionBtn("#2a2000", "#fcb31f", "1px solid #3a2f00")} title="Send to preservation pipeline">🖌</button>
        </>
      ) : isReady && onHandOff ? (
        // Photo's already on — 🖼 swaps it (wrong photo, bad shot) before hand-off.
        <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
          <button onClick={onLibrary} style={actionBtn("#1a2a1a", "#70be44", "1px solid #2a4a2a")} title="Swap the photo">🖼</button>
          <button onClick={onHandOff} style={actionBtn("#1a3a1a", "#70be44", "1px solid #2a5a2a")} title="Handed off">✓</button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
          <button onClick={onLibrary} style={actionBtn("#1a2a1a", "#70be44", "1px solid #2a4a2a")} title="Upload from photos">🖼</button>
          <button onClick={onCamera}  style={actionBtn("#2a1f00", "#fcb31f", "1px solid #3a2f00")} title="Take photo">📷</button>
        </div>
      )}
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────────────────────
function btn(bg, color) {
  return { background: bg, color, border: "none", borderRadius: "12px", padding: "0.85rem 1.5rem", fontSize: "1rem", fontWeight: "bold", cursor: "pointer", fontFamily: FONT.body };
}
function actionBtn(bg, color, border) {
  return { background: bg, color, border, borderRadius: "8px", padding: "0.5rem 0.65rem", fontSize: "1.1rem", cursor: "pointer", lineHeight: 1 };
}
const hud = { background: "transparent", border: `1px solid ${BRAND.border}`, color: BRAND.muted, borderRadius: "8px", padding: "0.4rem 0.75rem", fontSize: "0.8rem", cursor: "pointer" };
const sectionHeader = { color: BRAND.muted, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.6rem", fontWeight: "bold" };
